import { CSSProperties, FC, useRef } from 'react';
import { useHubspotForm } from '@aaronhayes/react-use-hubspot-form';
import { Helmet } from 'react-helmet';
import { OtpForm } from '@/components';
import { COMMON_CONTAINER_ID, navigationPages } from '@/config';
import { UserPreferenceEnum } from '@/api';
import { ToastContainer } from 'react-toastify';

import bg from '@/assets/images/verifi-signup/bg.svg';
import amlbotLogo from '@/assets/images/logo-amlbot.svg';
import verifiLogo from '@/assets/images/verifi-signup/logo-verifi-wallet.png';
import useDevice from '@/hooks/useDevice';

import styles from './OtpAuth.module.sass';

const PORTAL_ID = (import.meta.env.VITE_REACT_APP_HUBSPOT_PORTAL_ID as string)!;
const SIGNUP_COMPANY_FORM_ID = (import.meta.env
  .VITE_REACT_APP_HUBSPOT_SIGNUP_COMPANY_FORM_ID as string)!;

const formDivId = 'hubspot-form-wrapper';

const OtpAuth: FC = () => {
  const { isMobile } = useDevice();
  const hubspotFormRef = useRef<HTMLFormElement | null>(null);

  const { formCreated } = useHubspotForm({
    portalId: PORTAL_ID,
    formId: SIGNUP_COMPANY_FORM_ID,
    target: `#${formDivId}`,
    onFormReady: (form) => {
      hubspotFormRef.current = form;
    },
  });

  const handleBeforeVerify = (email: string, companyName: string) => {
    try {
      const form = hubspotFormRef.current;
      if (form) {
        const emailInput = form.querySelector(
          'input[name="email"]',
        ) as HTMLInputElement;
        const companyInput = form.querySelector(
          'input[name="company"]',
        ) as HTMLInputElement;
        emailInput.value = email;
        companyInput.value = companyName;
        form.submit();
      }
    } catch (err) {
      console.log('HUBSPOT ERROR', err);
    }
  };

  const handleSuccess = (token: string) => {
    window.location.href = navigationPages.TRACE_INVESTIGATIONS.path;
  };

  return (
    <>
      <Helmet>
        <title>AMLBot - Verif Wallet</title>
        <meta name="description" content="Verif Wallet signup" />
      </Helmet>

      <div
        className={styles.verifi}
        style={{ '--bg-image': `url(${bg})` } as CSSProperties}
      >
        <div className={styles.wrapper}>
          <div className={styles.content}>
            {isMobile && (
              <img
                src={amlbotLogo}
                alt="AMLBot"
                className={styles.amlbotLogo}
              />
            )}
            <div className={styles.label}>Free 60-Day Trial</div>
            <h1 className={styles.title}>
              Exclusive Offer for Verifi Wallet Users
            </h1>
            <p className={styles.description}>
              Get a Free 60-Day Trial Screening with AMLBot
            </p>

            {!isMobile && (
              <div className={styles.logos}>
                <img
                  src={amlbotLogo}
                  alt="AMLBot"
                  className={styles.amlbotLogo}
                />
                <img
                  src={verifiLogo}
                  alt="Verifi"
                  className={styles.verifiLogo}
                />
              </div>
            )}
          </div>

          <OtpForm
            preference={UserPreferenceEnum.AI_TRACE}
            showCompanyName
            stepTitles={{
              1: 'Start Checking & Investigating Wallets Today!',
              2: 'Enter the OTP code we sent to your email to continue',
            }}
            onBeforeVerify={handleBeforeVerify}
            onSuccess={handleSuccess}
            footer={
              isMobile ? (
                <img
                  src={verifiLogo}
                  alt="Verifi"
                  className={styles.verifiLogoBottom}
                />
              ) : null
            }
          />
        </div>
      </div>

      <ToastContainer
        containerId={COMMON_CONTAINER_ID}
        position="top-center"
        limit={3}
        enableMultiContainer
        hideProgressBar
      />
      <div id={formDivId} style={{ height: 0, width: 0 }} />
    </>
  );
};

export default OtpAuth;
